import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Gift, 
  Trophy, 
  Upload, 
  Video, 
  Image as ImageIcon, 
  Type,
  Sparkles,
  Check,
  ChevronRight,
  Heart,
  Play,
  X,
  Camera,
  Mic,
  Send,
  ArrowLeft,
  Eye,
  Smartphone,
  GripVertical
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL;

// Sortable media preview item for drag-to-reorder
const SortableMediaPreview = ({ preview, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: preview.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 group"
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`gift-drag-handle-${index}`}
      >
        <GripVertical className="w-3 h-3" strokeWidth={1.5} />
      </div>

      {preview.type === 'video' ? (
        <video
          src={preview.url}
          className="w-full h-full object-cover"
        />
      ) : (
        <img
          src={preview.url}
          alt={preview.name}
          className="w-full h-full object-cover"
        />
      )}
      <button
        onClick={() => onRemove(index)}
        className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
        data-testid={`gift-remove-media-${index}`}
      >
        <X className="w-4 h-4" />
      </button>
      {preview.type === 'video' && (
        <div className="absolute bottom-1 left-1 px-2 py-0.5 bg-black/50 rounded text-white text-xs flex items-center gap-1">
          <Play className="w-3 h-3" />
          Video
        </div>
      )}
    </div>
  );
};

const GiftUploadPage = () => {
  const { nfcCode } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tagInfo, setTagInfo] = useState(null);
  
  // Form data
  const [recipientName, setRecipientName] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviewUrls, setMediaPreviewUrls] = useState([]);
  
  // DnD sensors for drag-to-reorder
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = mediaPreviewUrls.findIndex((item) => item.id === active.id);
      const newIndex = mediaPreviewUrls.findIndex((item) => item.id === over.id);
      
      setMediaPreviewUrls(arrayMove(mediaPreviewUrls, oldIndex, newIndex));
      setMediaFiles(arrayMove(mediaFiles, oldIndex, newIndex));
      toast.success('Media reordered');
    }
  };
  
  // Check if tag exists
  useEffect(() => {
    const checkTag = async () => {
      try {
        const response = await axios.get(`${API}/tags/${nfcCode}`);
        setTagInfo(response.data);
        
        // If tag already has content, show it
        if (response.data.message || (response.data.media_files && response.data.media_files.length > 0)) {
          setTitle(response.data.title || '');
          setMessage(response.data.message || '');
        }
      } catch (error) {
        // Tag doesn't exist or no content yet - that's fine
        console.log('Tag ready for content');
      }
    };
    checkTag();
  }, [nfcCode]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limit to 5 files
    const newFiles = files.slice(0, 5 - mediaFiles.length);
    
    // Create preview URLs with unique IDs for drag-to-reorder
    const newPreviews = newFiles.map((file, idx) => ({
      id: `media-${Date.now()}-${idx}`,
      url: URL.createObjectURL(file),
      type: file.type.startsWith('video') ? 'video' : 'image',
      name: file.name
    }));
    
    setMediaFiles([...mediaFiles, ...newFiles]);
    setMediaPreviewUrls([...mediaPreviewUrls, ...newPreviews]);
  };

  const removeMedia = (index) => {
    URL.revokeObjectURL(mediaPreviewUrls[index].url);
    setMediaFiles(mediaFiles.filter((_, i) => i !== index));
    setMediaPreviewUrls(mediaPreviewUrls.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!message.trim() && mediaFiles.length === 0) {
      toast.error('Please add a message or upload some media');
      return;
    }

    setLoading(true);
    
    try {
      // Create FormData
      const formData = new FormData();
      formData.append('title', title || `A special message for ${recipientName || 'you'}`);
      formData.append('message', message);
      
      // Add media files
      mediaFiles.forEach(file => {
        formData.append('files', file);
      });

      // Upload content
      await axios.post(`${API}/upload/${nfcCode}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSubmitted(true);
      toast.success('Your message has been saved!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step < 3) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-6">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Check className="w-12 h-12 text-white" />
          </motion.div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            You're All Set!
          </h1>
          
          <p className="text-gray-600 mb-8">
            Your special message has been saved. When the recipient taps or scans the tag, 
            they'll see your heartfelt message and media.
          </p>

          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Smartphone className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-purple-900">Preview Your Message</span>
            </div>
            <a
              href={`/view/${nfcCode}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-xl text-purple-600 font-medium hover:shadow-md transition-all"
            >
              <Eye className="w-5 h-5" />
              View as Recipient
            </a>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => {
                setSubmitted(false);
                setStep(1);
                setMessage('');
                setMediaFiles([]);
                setMediaPreviewUrls([]);
              }}
              className="w-full py-3 border-2 border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Edit Message
            </button>
            <a
              href="/"
              className="block w-full py-3 text-gray-500 hover:text-gray-700 transition-colors"
            >
              Return to ScanIt2Go
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-gray-900">ScanIt2Go</span>
          </a>
          
          {/* Progress indicator */}
          <div className="flex items-center gap-2">
            {[1, 2, 3].map(s => (
              <div
                key={s}
                className={`w-8 h-1 rounded-full transition-colors ${
                  s <= step ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          {/* Step 1: Welcome & Recipient */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <Heart className="w-10 h-10 text-white" />
                </motion.div>
                <h1 className="text-3xl font-bold text-gray-900 mb-3">
                  Create Something Special
                </h1>
                <p className="text-gray-600">
                  Add a personal video message, photos, or heartfelt text that will come alive when your gift, card, or item is opened.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Who is this for? (optional)
                    </label>
                    <input
                      type="text"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Recipient's name"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your name (optional)
                    </label>
                    <input
                      type="text"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      placeholder="Your name"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* What you can add */}
              <div className="bg-white/50 rounded-2xl p-6 mb-8">
                <h3 className="font-semibold text-gray-900 mb-4">What you can add:</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-white rounded-xl">
                    <Video className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <span className="text-sm text-gray-600">Video Message</span>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl">
                    <ImageIcon className="w-8 h-8 text-pink-600 mx-auto mb-2" />
                    <span className="text-sm text-gray-600">Photos</span>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl">
                    <Type className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                    <span className="text-sm text-gray-600">Written Message</span>
                  </div>
                </div>
              </div>

              <button
                onClick={nextStep}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              >
                Get Started
                <ChevronRight className="w-5 h-5" />
              </button>
            </motion.div>
          )}

          {/* Step 2: Add Content */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={prevStep}
                className="flex items-center gap-2 text-gray-600 mb-6 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Add Your Message
              </h2>
              <p className="text-gray-600 mb-6">
                Share photos, videos, and a heartfelt message
              </p>

              {/* Title */}
              <div className="bg-white rounded-2xl p-6 shadow-lg mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title (optional)
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={`Happy Birthday${recipientName ? ', ' + recipientName : ''}!`}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                />
              </div>

              {/* Message */}
              <div className="bg-white rounded-2xl p-6 shadow-lg mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write something from the heart..."
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none"
                />
                <div className="flex justify-end mt-2">
                  <span className="text-sm text-gray-400">{message.length} characters</span>
                </div>
              </div>

              {/* Media Upload */}
              <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Photos & Videos (optional)
                </label>
                
                {/* Preview Grid with Drag-to-Reorder */}
                {mediaPreviewUrls.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">Drag to reorder photos</p>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={mediaPreviewUrls.map(p => p.id)}
                        strategy={rectSortingStrategy}
                      >
                        <div className="grid grid-cols-3 gap-3">
                          {mediaPreviewUrls.map((preview, index) => (
                            <SortableMediaPreview
                              key={preview.id}
                              preview={preview}
                              index={index}
                              onRemove={removeMedia}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}

                {/* Upload Button */}
                {mediaFiles.length < 5 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-8 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-purple-400 hover:bg-purple-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <Upload className="w-6 h-6 text-purple-600" />
                    </div>
                    <span className="text-gray-600">Click to upload photos or videos</span>
                    <span className="text-sm text-gray-400">Max 5 files • 50MB each</span>
                  </button>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              <button
                onClick={nextStep}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              >
                Preview
                <ChevronRight className="w-5 h-5" />
              </button>
            </motion.div>
          )}

          {/* Step 3: Preview & Submit */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={prevStep}
                className="flex items-center gap-2 text-gray-600 mb-6 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Preview Your Gift
              </h2>
              <p className="text-gray-600 mb-6">
                This is what the recipient will see
              </p>

              {/* Preview Card */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
                {/* Media Preview */}
                {mediaPreviewUrls.length > 0 && (
                  <div className="aspect-video bg-gray-100 relative">
                    {mediaPreviewUrls[0].type === 'video' ? (
                      <video
                        src={mediaPreviewUrls[0].url}
                        controls
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <img
                        src={mediaPreviewUrls[0].url}
                        alt="Preview"
                        className="w-full h-full object-contain"
                      />
                    )}
                    {mediaPreviewUrls.length > 1 && (
                      <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/60 rounded-full text-white text-sm">
                        +{mediaPreviewUrls.length - 1} more
                      </div>
                    )}
                  </div>
                )}
                
                <div className="p-6">
                  {title && (
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
                  )}
                  {message && (
                    <p className="text-gray-700 whitespace-pre-wrap">{message}</p>
                  )}
                  {senderName && (
                    <p className="mt-4 text-gray-500">— {senderName}</p>
                  )}
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-purple-50 rounded-xl p-4 mb-6 flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-purple-900">
                  <p className="font-medium">Ready to create magic?</p>
                  <p className="text-purple-700">Once saved, the recipient can tap the NFC tag or scan the QR code to see your message.</p>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={loading || (!message.trim() && mediaFiles.length === 0)}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Save & Finish
                  </>
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="max-w-2xl mx-auto px-6 py-8 text-center">
        <p className="text-sm text-gray-500">
          Powered by{' '}
          <a href="https://scanit2go.com" className="text-purple-600 hover:underline">
            scanit2go.com
          </a>
        </p>
      </footer>
    </div>
  );
};

export default GiftUploadPage;
